<script setup>
import { ref, watch, onMounted, onBeforeUnmount, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useModalStore } from "@/stores/ModalStore.js"
import { useSuppliersStore } from "@/stores/storage/SuppliersStore.js"
import { showSuccessToast, showErrorToast } from "@/utils/toast"

const { t } = useI18n()
const modalStore = useModalStore()
const suppliersStore = useSuppliersStore()

/** Modal identifier used for opening/closing this modal */
const THIS_MODAL_NAME = 'addSupplier'

/** Supplier name input */
const supplierName = ref('')

/** Form validation errors */
const nameError = ref('')

/** Reference to the form for validation */
const formRef = ref(null)

/**
 * Reset form to initial state
 *
 * @returns {void}
 */
const resetForm = () => {
  supplierName.value = ''
  nameError.value = ''
}

/**
 * Validate the form
 *
 * @returns {boolean} True if valid
 */
const validateForm = () => {
  nameError.value = ''
  
  if (!supplierName.value.trim()) {
    nameError.value = t('validmsg.required')

    return false
  }
  
  if (supplierName.value.trim().length < 2) {
    nameError.value = t('validmsg.min')

    return false
  }
  
  return true
}

/**
 * Close the modal without saving
 *
 * @returns {void}
 */
const closeModal = () => {
  resetForm()
  modalStore.closeModal(THIS_MODAL_NAME)
}

/**
 * Check if save button should be disabled
 *
 * @returns {boolean}
 */
const isSaveDisabled = () => {
  return suppliersStore.isSaving || !supplierName.value.trim()
}

/**
 * Handle save action - create new supplier
 *
 * @returns {Promise<void>}
 */
const handleSave = async () => {
  if (!validateForm()) {
    return
  }

  const result = await suppliersStore.saveSupplier({
    name: supplierName.value.trim(),
  })
  suppliersStore.isSaving = true // prevent form unlocking

  if (result.success) {
    showSuccessToast(result.message || t('storage.suppliers.supplier_saved'))
    await suppliersStore.refreshSuppliers()
    suppliersStore.isSaving = false
    modalStore.closeModal(THIS_MODAL_NAME, result.data?.supplier || null)
  } else {
    suppliersStore.isSaving = false
  }
}

onMounted(() => {
  if (modalStore.modalParams.addSupplier?.query?.length) {
    suppliersStore.isSaving = true
    supplierName.value = modalStore.modalParams.addSupplier?.query
    setTimeout(() => { // B: bo się zdarza że enter w VAutocomplete wywolywal zapis
      suppliersStore.isSaving = false
    }, 200)
  }
})

onUnmounted(() => {
  resetForm()
})
</script>

<template>
  <VDialog
    v-model="modalStore.modals.addSupplier"
    max-width="500px"
    persistent
  >
    <VCard :loading="suppliersStore.isSaving">
      <VCardTitle class="border-b-thin">
        <div class="d-flex justify-space-between align-center pa-1">
          {{ $t('storage.suppliers.add_supplier') }}
          <VIcon
            class="ml-auto cursor-pointer"
            :disabled="suppliersStore.isSaving"
            icon="ri-close-line"
            @click="closeModal"
          />
        </div>
      </VCardTitle>

      <VCardText class="pt-4">
        <VRow>
          <VCol cols="12">
            <VTextField
              v-model="supplierName"
              :label="$t('storage.suppliers.supplier_name')"
              :placeholder="$t('storage.suppliers.supplier_name_placeholder')"
              :error-messages="nameError"
              :disabled="suppliersStore.isSaving"
              autofocus
              @keyup.enter="handleSave"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VCardActions class="border-t-thin">
        <div class="d-flex pa-2 flex-fill justify-space-between">
          <VBtn
            variant="outlined"
            color="secondary"
            :disabled="suppliersStore.isSaving"
            @click="closeModal"
          >
            {{ $t('cancel') }}
          </VBtn>
          <VBtn
            color="green"
            variant="flat"
            :disabled="isSaveDisabled()"
            :loading="suppliersStore.isSaving"
            @click="handleSave"
          >
            {{ $t('save') }}
          </VBtn>
        </div>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
